// HISTORY — one list for everything that happened to an invoice (owner,
// 2026-10-08 — `docs/decisions.md` D-329): *"combine Decision History and
// Comments into one History section"*.
//
// A TIMELINE SINCE D-330 (owner, 2026-10-08, from the approved mockup). Each
// row is a coloured dot on a gray line, then:
//   1. the ACT, verb first and bold, " by <name>" after it, and the time at the
//      right in Eastern time ("Jul 30, 2026 · 9:30 AM");
//   2. a small gray caption: the step's name, then " · " and the category a
//      hold or a decision was given;
//   3. the detail in gray boxes: a comment, a decision's or a hold's comment,
//      the Total's old and new amounts.
//
// THE FIRST ROW IS THE UPLOAD: "Uploaded by <name>", captioned "Upload · <file
// name>, pages 3–4 of 12", or "Received by email from <address>". The AI's
// notes sit under it as up to three boxes, text only, in splitting,
// extracting, matching order (owner, 2026-10-08: "let's just get rid of the
// separate AI Notes section … The three comments from those agents can just
// sit under the Upload label as three separate comments", and "I think that
// you can drop the labels within there for now."). A missing note is left out.
//
// FOLDED SINCE D-343 (owner, 2026-10-09: "The AI notes can be a bit
// overwhelming at the top"): the upload row shows one sentence and a "Click
// here for details." that opens the notes beneath it. "The invoice was
// received and prepared without issue." when every note is its pass's default
// sentence; "This invoice has some notes attached, regarding the intake
// process." when any note says something else.
//
// The rows come from the detail bundle: `upload`, `decisions`, `holds`,
// `total_corrections`, the comment thread, and the invoice header (its AI notes
// and page span). This module is pure — it turns those rows into words and
// colours, so the tests can pin them without a DOM.
// `components/InvoiceHistory.tsx` draws them.
//
// THE COLOURS (the owner's list, completed for the outcomes he did not name):
//   green  — Released / Approved, Reinstated, and Typed Into Point
//            Click Care: the invoice moved on, or finished;
//   red    — Denied, and the three archives (owner, 2026-10-08, D-329
//            amended: "Archives can be red, as well. They effectively kill an
//            entry, so that is logically consistent.");
//   blue   — a comment;
//   yellow — the Total changed (the "1 line item needs action" pill's colour);
//   gray   — everything else: the upload, a notification sent, a hold placed or
//            released, a skip for inactivity, and a routing ("Routed to …", a
//            later change).
//
// A NOTIFICATION SENT ABOUT THE UPLOAD IS A ROW (owner, 2026-10-08 — D-335: *"Can we
// also add "Alert sent to x, y, and z" actions to the timeline? The finer text
// underneath the action can name the alert."*): "Notification sent to Alex Morgan,
// Bailey Nakamura, and Casey Okonkwo", captioned "New Invoices Upload". Each
// recipient by name where one is known, else by address.
//
// EVERY RELEASE AND REINSTATE SHOWS HOW ITS ROUTE WAS DECIDED (owner,
// 2026-10-12 — D-350: "Routing rules should only get checked at Release and
// Reinstate, and that routing decision gets reflected in History as such.").
// Three kinds of row, after the decision that decided it: "Route overridden by
// <who set it>", captioned with the step, saying the route and any vendor rule
// it wrote (no category and no comment since D-350's amendment); "Route decided by a rule" as below; and
// "Route decided" for the default route, which until then wrote no row.
//
// ONE ROUTE ROW, WHERE THE ROUTE IS DECIDED (owner, 2026-10-09 — D-343): "it
// only shows after an invoice is Released or Reinstated and defines the full
// path from there". The rows below at every approval went; so did the bold "by
// a rule" — the act is "Route decided", in bold, and "by a rule" reads plain,
// as "by Dave Dodson" does. One rule shows its sentence. Several show the
// combined route ("When multiple rules apply, …") and a "Click here for
// details." that opens each rule's own sentence beneath it.
//
// WHAT D-336 SAID, kept for the reason behind a route row at all
// (owner, 2026-10-09 — D-336: "an invoice
// that meets any routing rule would have each of its routing decisions
// reported in the invoice modal's History timeline", and "An invoice that
// follows the default path due to not matching any rule does not need History
// entries dictating its moves."): one gray row per rule on each forward move
// it routed — the release, an approval or a reinstate — "Routed by a rule",
// captioned with the step, the rule's sentence and the approvals it requires in
// the box. A rule that matches at several moves has a row at each (owner,
// 2026-10-09: "if an invoice is at all being steered by a rule at any time, I
// would like to see that in the timeline."). None on the default path.
//
// PORT NOTE, carried from `DecisionHistory.tsx`: every decision row here
// changed the invoice. A refused decision writes nothing, so there is no
// "Not applied" row to show.
import { NOTIFICATION_TYPE_LABEL, DECISION_OUTCOME, EASTERN_TIME_ZONE, isDefaultAiNote, type NotificationType } from '@ap/shared'
import type { Conversation, Correction, Decision, Hold, Invoice, InvoiceNotificationSummary, InvoiceUploadSummary } from './types'
import { DecisionStep, reasonCategoryLabel, STEP_LABEL } from './constants'
import { escalatedLine, formatCurrency } from './format'
import { combinedRoutingSentence, routingRuleSentenceWithApprovals } from './oversightRuleSentence'
import { routeList } from './routeOverride'
import { TINT_PILLS, type PillColors } from './theme'

export type HistoryTone = 'green' | 'red' | 'blue' | 'yellow' | 'gray'

/** What a row records. `routed` is a routing rule that matched (D-336). */
type HistoryKind = 'upload' | 'notification' | 'decision' | 'skip' | 'hold' | 'hold-released' | 'total' | 'comment' | 'routed'

/** One History row: what the timeline draws, in the order it draws it. */
export interface HistoryEntry {
  key: string
  kind: HistoryKind
  /** When it happened (ISO), or null when the record holds no time. The list
      is oldest first; the upload row always leads. */
  at: string | null
  tone: HistoryTone
  /** The act, verb first, drawn bold: "Released", "Comment posted",
      "Received by email". */
  action: string
  /** Who, drawn after the act as " by <name>" (" from <address>" for an
      emailed upload, " to <names>" for a notification). Null when nobody acted — a
      skip for inactivity. */
  actor: string | null
  /** The word before `actor`. */
  actorJoin: 'by' | 'from' | 'to'
  /** "Jul 30, 2026 · 9:30 AM", Eastern; '' when `at` is null. */
  when: string
  /** The small gray line: the step, then " · " and a category; or, for the
      upload, "Upload · <file>, pages 3–4 of 12". Null when there is nothing. */
  caption: string | null
  /** The gray boxes, in order: a comment, a reason, the Total's amounts.
      Empty when there is nothing to add. */
  details: string[]
  /** A box that ends in "Click here for details." and opens `items` beneath
      it, set apart as belonging to it (D-343): the AI's notes on the upload,
      each rule behind a combined route. Drawn after `details`. */
  more?: { lead: string; items: string[] }
}

/** "Import Control Decision" → "Import Control", "Department Approval
    Decision" → "Manager Approval" (owner item 8, 2026-08-19). `STEP_LABEL`
    first, since it is the one place a rename lives; the trailing " Decision"
    strip is the fallback for a step it does not name. Null for no step. */
function historyStepLabel(step: string | null | undefined): string | null {
  if (!step) return null
  return STEP_LABEL[step] ?? step.replace(/ Decision$/, '')
}

/**
 * Outcomes whose stored value does not read right Title-Cased. The divert is
 * named the same way everywhere it appears — the button, the confirmation, the
 * thread and here (owner, 2026-08-31). "Typed Into Point Click Care"
 * since 2026-10-07 (D-311), the words of the Export Control button. An archive
 * reads **Archived**, with its Category — which is the status it reached — in
 * the row's caption (D-362). DISPLAY ONLY — the stored outcomes are unchanged.
 */
const OUTCOME_LABEL: Readonly<Record<string, string>> = {
  [DECISION_OUTCOME.requiresManualEntry]: 'Typed Into Point Click Care',
  [DECISION_OUTCOME.archived]: 'Archived',
  [DECISION_OUTCOME.archivedWithoutPayment]: 'Payment Denied',
  [DECISION_OUTCOME.archivedForFreshInvoice]: 'Revised Invoice Requested',
}

/** "approved" → "Approved"; at Import Control, "Released", the button's word
    (owner, 2026-10-08: "Import Control - Released by Alex Morgan"). */
export function historyOutcomeLabel(decision: string | null | undefined, step?: string | null): string {
  if (!decision) return 'Decided'
  if (decision === DECISION_OUTCOME.approved && step === DecisionStep.ImportControl) return 'Released'
  return OUTCOME_LABEL[decision] ?? decision.charAt(0).toUpperCase() + decision.slice(1)
}

/** The colour of a decision's row. */
export function decisionTone(decision: string | null | undefined): HistoryTone {
  switch (decision) {
    case DECISION_OUTCOME.approved:
    case DECISION_OUTCOME.reinstated:
    case DECISION_OUTCOME.requiresManualEntry:
      return 'green'
    case DECISION_OUTCOME.denied:
    case DECISION_OUTCOME.archived:
    case DECISION_OUTCOME.archivedWithoutPayment:
    case DECISION_OUTCOME.archivedForFreshInvoice:
      return 'red'
    default:
      return 'gray'
  }
}

/** The dot's colour for each tone — the pills' own dots, so a History row
    and the pill above it are one colour; gray is drawn from a neutral token. */
export const HISTORY_TONE_COLOR: Readonly<Record<Exclude<HistoryTone, 'gray'>, string>> = {
  green: TINT_PILLS.green.dot,
  red: TINT_PILLS.red.dot,
  blue: TINT_PILLS.blue.dot,
  yellow: TINT_PILLS.yellow.dot,
}

/** "Jul 30, 2026 · 9:30 AM", in Eastern time, the organization's clock. */
export function historyWhen(value: string | null | undefined): string {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const date = d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: EASTERN_TIME_ZONE,
  })
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: EASTERN_TIME_ZONE })
  return `${date} · ${time}`
}

const trimmed = (text: string | null | undefined): string | null => {
  const t = (text ?? '').trim()
  return t === '' ? null : t
}

const person = (name: string | null | undefined, email: string | null | undefined): string =>
  trimmed(name) ?? trimmed(email) ?? 'Unknown'

/** A Total as its box shows it; a blank Total reads "blank". */
function totalText(value: string | null | undefined): string {
  const t = trimmed(value)
  return t === null || Number.isNaN(Number(t)) ? 'blank' : formatCurrency(t)
}

/** "Director Approval · Price Discrepancy"; either half may be missing. */
function caption(step: string | null | undefined, category: string | null | undefined): string | null {
  const parts = [historyStepLabel(step), reasonCategoryLabel(category) || null].filter((p): p is string => !!p)
  return parts.length === 0 ? null : parts.join(' · ')
}

/** "pages 3–4 of 12", "page 3 of 12", "pages 3–4"; null with no span. */
export function uploadPagesPhrase(start: number | null | undefined, end: number | null | undefined, count: number | null | undefined): string | null {
  if (start == null && end == null) return null
  const first = start ?? end!
  const last = end ?? start!
  const span = first === last ? `page ${first}` : `pages ${first}–${last}`
  return count != null && count > 0 ? `${span} of ${count}` : span
}

/** The upload row's caption: "Upload · July statements.pdf, pages 3–4 of 12". */
function uploadCaption(fileName: string | null | undefined, pages: string | null): string {
  const what = [trimmed(fileName), pages].filter((p): p is string => !!p).join(', ')
  return what ? `Upload · ${what}` : 'Upload'
}

/** The three AI notes, as stored, in pipeline order; a missing one is left out (D-250). */
export function aiNoteDetails(invoice: Pick<Invoice, 'ai_splitting_notes' | 'ai_extracting_notes' | 'ai_matching_notes'>): string[] {
  return [invoice.ai_splitting_notes, invoice.ai_extracting_notes, invoice.ai_matching_notes]
    .map((n) => trimmed(n))
    .filter((n): n is string => n !== null)
}

/** The one sentence the upload row shows over its folded notes (D-343). */
export function aiNotesSummary(notes: readonly string[]): string {
  return notes.every(isDefaultAiNote)
    ? 'The invoice was received and prepared without issue.'
    : 'This invoice has some notes attached, regarding the intake process.'
}

/** The first row: the upload, with the AI's notes folded under it (D-330, D-343). */
export function uploadEntry(
  invoice: Pick<
    Invoice,
    'id' | 'uploader_email' | 'received_at' | 'upload_page_start' | 'upload_page_end' | 'ai_splitting_notes' | 'ai_extracting_notes' | 'ai_matching_notes'
  >,
  upload: InvoiceUploadSummary | null | undefined,
): HistoryEntry {
  const at = upload?.received_at ?? invoice.received_at ?? null
  const pages = uploadPagesPhrase(invoice.upload_page_start, invoice.upload_page_end, upload?.page_count)
  const address = trimmed(upload?.from_address) ?? trimmed(invoice.uploader_email)
  const notes = aiNoteDetails(invoice)
  const base = {
    key: `upload-${invoice.id}`,
    kind: 'upload' as const,
    at,
    tone: 'gray' as const,
    when: historyWhen(at),
    caption: uploadCaption(upload?.file_name, pages),
    details: [] as string[],
    more: notes.length === 0 ? undefined : { lead: aiNotesSummary(notes), items: notes },
  }
  if (upload?.channel === 'app') {
    return { ...base, action: 'Uploaded', actor: trimmed(upload.uploaded_by_name) ?? address, actorJoin: 'by' }
  }
  if (upload?.channel === 'email') {
    return { ...base, action: 'Received by email', actor: address, actorJoin: 'from' }
  }
  // The upload could not be read: say only what the invoice itself records.
  return { ...base, action: 'Received', actor: address, actorJoin: 'from' }
}

/** "A", "A and B", "A, B, and C" — the Oxford comma, as the house style has it. */
export function oxfordList(items: readonly string[]): string {
  if (items.length <= 1) return items.join('')
  if (items.length === 2) return `${items[0]} and ${items[1]}`
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`
}

/** The notification's name, as the Notifications screen shows it; the stored value if unknown. */
function notificationLabel(type: string): string {
  return (NOTIFICATION_TYPE_LABEL as Record<string, string>)[type as NotificationType] ?? type
}

/** One "Alert sent to …" row (D-335): each recipient by name, else by address. */
function notificationEntry(notification: InvoiceNotificationSummary, index: number): HistoryEntry & { rank: number } {
  const names = notification.recipients.map((r) => trimmed(r.name) ?? r.email)
  return {
    key: `notification-${notification.notification_type}-${index}`,
    kind: 'notification',
    at: notification.sent_at,
    rank: 0,
    tone: 'gray',
    action: 'Notification sent',
    actor: names.length > 0 ? oxfordList(names) : null,
    actorJoin: 'to',
    when: historyWhen(notification.sent_at),
    caption: notificationLabel(notification.notification_type),
    details: [],
  }
}

interface HistorySources {
  decisions: readonly Decision[]
  holds: readonly Hold[]
  comments: readonly Conversation[]
  totalChanges: readonly Correction[]
  /** The upload row's sources; without them there is no upload row. */
  invoice?: Parameters<typeof uploadEntry>[0]
  upload?: InvoiceUploadSummary | null
  /** The notifications sent about the upload (D-335) and about the invoice (D-336). */
  notifications?: readonly InvoiceNotificationSummary[]
}

/** Every row, the upload first, then oldest first. Two rows at the same
    instant keep the order the sources are listed in, then their ids, so the
    list never reshuffles. */
export function buildHistory({ decisions, holds, comments, totalChanges, invoice, upload, notifications = [] }: HistorySources): HistoryEntry[] {
  const rows: (HistoryEntry & { rank: number })[] = notifications.map(notificationEntry)
  for (const d of decisions) {
    if (d.decision === DECISION_OUTCOME.escalated) {
      // A skip for inactivity keeps its own words, complete sentences with no
      // actor (D-235): "Manager Approval was skipped due to inactivity." in
      // bold, the time at the right, and its stored second sentence in the box.
      const reason = trimmed(d.reason)
      rows.push({
        key: `decision-${d.id}`,
        kind: 'skip',
        at: d.decided_at,
        rank: 0,
        tone: 'gray',
        action: escalatedLine(historyStepLabel(d.step) ?? 'The step', '', null),
        actor: null,
        actorJoin: 'by',
        when: historyWhen(d.decided_at),
        caption: null,
        details: reason === null ? [] : [/[.!?]$/.test(reason) ? reason : `${reason}.`],
      })
      continue
    }
    rows.push({
      key: `decision-${d.id}`,
      kind: 'decision',
      at: d.decided_at,
      rank: 0,
      tone: decisionTone(d.decision),
      action: historyOutcomeLabel(d.decision, d.step),
      actor: person(d.decided_by_name, d.decided_by_email),
      actorJoin: 'by',
      when: historyWhen(d.decided_at),
      caption: caption(d.step, d.reason_category),
      details: [trimmed(d.reason)].filter((t): t is string => t !== null),
    })
    // ONE ROW FOR THE ROUTE, right after the release or reinstate that decided
    // it (same instant, and "decision-" sorts before "routed-"), and none at the
    // approvals after it (D-343, D-350).
    const rules = d.routing_rules ?? []
    const decidesRoute =
      (d.decision === DECISION_OUTCOME.approved && d.step === DecisionStep.ImportControl) ||
      d.decision === DECISION_OUTCOME.reinstated
    const route = d.route ?? null
    if (decidesRoute && route?.source === 'override' && route.override) {
      // A person chose it, in the Override pop-up (D-350).
      const vendor = route.override.rule_vendor_name?.trim() || 'this vendor'
      const ruleNote =
        route.override.rule_action === 'created'
          ? `A rule was created, so this route is now the default for ${vendor}.`
          : route.override.rule_action === 'updated'
            ? `The rule for ${vendor} was updated, so this route is now its default.`
            : null
      rows.push({
        key: `routed-${d.id}`,
        kind: 'routed',
        at: d.decided_at,
        rank: 0,
        tone: 'gray',
        action: 'Route overridden',
        actor: trimmed(route.override.set_by_name),
        actorJoin: 'by',
        when: historyWhen(d.decided_at),
        caption: historyStepLabel(d.step),
        details: [`This invoice will require ${routeList(route)}.`, ruleNote].filter((t): t is string => t !== null),
      })
    } else if (decidesRoute && rules.length === 0 && route?.source === 'default') {
      // No Override, and no rule matched: the default route (D-350).
      rows.push({
        key: `routed-${d.id}`,
        kind: 'routed',
        at: d.decided_at,
        rank: 0,
        tone: 'gray',
        action: 'Route decided',
        actor: null,
        actorJoin: 'by',
        when: historyWhen(d.decided_at),
        caption: historyStepLabel(d.step),
        details: [`No routing rule applied, so this invoice follows the default route, which requires ${routeList(route)}.`],
      })
    } else if (decidesRoute && rules.length > 0) {
      const sentences = rules.map((rule) => routingRuleSentenceWithApprovals(rule))
      rows.push({
        key: `routed-${d.id}`,
        kind: 'routed',
        at: d.decided_at,
        rank: 0,
        tone: 'gray',
        action: 'Route decided',
        actor: 'a rule',
        actorJoin: 'by',
        when: historyWhen(d.decided_at),
        caption: historyStepLabel(d.step),
        details: rules.length === 1 ? sentences : [],
        more: rules.length === 1 ? undefined : { lead: combinedRoutingSentence(rules), items: sentences },
      })
    }
  }
  for (const h of holds) {
    rows.push({
      key: `hold-${h.id}`,
      kind: 'hold',
      at: h.held_at,
      rank: 1,
      tone: 'gray',
      action: 'Held',
      actor: person(h.held_by_name, h.held_by_email),
      actorJoin: 'by',
      when: historyWhen(h.held_at),
      caption: caption(h.step, h.reason_category),
      details: [trimmed(h.reason)].filter((t): t is string => t !== null),
    })
    if (h.released_at) {
      rows.push({
        key: `hold-released-${h.id}`,
        kind: 'hold-released',
        at: h.released_at,
        rank: 1,
        tone: 'gray',
        action: 'Hold released',
        actor: person(h.released_by_name, h.released_by_email),
        actorJoin: 'by',
        when: historyWhen(h.released_at),
        caption: historyStepLabel(h.step),
        details: [],
      })
    }
  }
  for (const c of totalChanges) {
    rows.push({
      key: `total-${c.id}`,
      kind: 'total',
      at: c.corrected_at,
      rank: 2,
      tone: 'yellow',
      action: 'Total changed',
      actor: person(c.corrected_by_name, c.corrected_by_email),
      actorJoin: 'by',
      when: historyWhen(c.corrected_at),
      caption: historyStepLabel(c.step),
      details: [`${totalText(c.old_value)} → ${totalText(c.new_value)}`],
    })
  }
  for (const c of comments) {
    rows.push({
      key: `comment-${c.id}`,
      kind: 'comment',
      at: c.created_at,
      rank: 3,
      tone: 'blue',
      // "Let's use "Comment posted" in the timeline" (owner, 2026-10-08).
      action: 'Comment posted',
      actor: person(c.author_name, c.author_email),
      actorJoin: 'by',
      when: historyWhen(c.created_at),
      caption: historyStepLabel(c.step),
      details: [c.body],
    })
  }
  const time = (iso: string | null) => {
    const t = iso === null ? NaN : new Date(iso).getTime()
    return Number.isNaN(t) ? 0 : t
  }
  rows.sort((a, b) => time(a.at) - time(b.at) || a.rank - b.rank || a.key.localeCompare(b.key))
  const sorted = rows.map(({ rank: _rank, ...entry }) => entry)
  return invoice ? [uploadEntry(invoice, upload), ...sorted] : sorted
}

/** The distinct people among `who`, by address and then by name, first seen first. */
function distinctPeople(who: readonly { name: string | null | undefined; email: string | null | undefined }[]): string[] {
  const seen = new Map<string, string>()
  for (const w of who) {
    const key = (trimmed(w.email) ?? trimmed(w.name) ?? '').toLowerCase()
    if (key && !seen.has(key)) seen.set(key, person(w.name, w.email))
  }
  return [...seen.values()]
}

/** "Alex Morgan changed the Total" / "2 users changed the Total"; null when
    nobody has. Counted by person, not by change. */
export function totalChangedPillLabel(changes: readonly Correction[]): string | null {
  const people = distinctPeople(changes.map((c) => ({ name: c.corrected_by_name, email: c.corrected_by_email })))
  if (people.length === 0) return null
  return people.length === 1 ? `${people[0]} changed the Total` : `${people.length} users changed the Total`
}

/** One written comment, whoever wrote it and wherever: who, for the count. */
interface WrittenComment {
  name: string | null | undefined
  email: string | null | undefined
}

/**
 * EVERY WRITTEN COMMENT A PERSON LEFT (owner, 2026-10-08 — D-330: "I would
 * include all of them in the count."): each posted comment, and the free-text
 * comment on a decision (Deny, Reinstate, Archive, Typed Into Point Click Care …) or on a
 * hold. A decision or hold with no text left none. A skip for inactivity is the
 * system's, not a person's, and the AI's notes are not counted.
 */
export function writtenComments(sources: Pick<HistorySources, 'decisions' | 'holds' | 'comments'>): WrittenComment[] {
  return [
    ...sources.comments.map((c) => ({ name: c.author_name, email: c.author_email })),
    ...sources.decisions
      .filter((d) => d.decision !== DECISION_OUTCOME.escalated && trimmed(d.reason) !== null)
      .map((d) => ({ name: d.decided_by_name, email: d.decided_by_email })),
    ...sources.holds
      .filter((h) => trimmed(h.reason) !== null)
      .map((h) => ({ name: h.held_by_name, email: h.held_by_email })),
  ]
}

/** "Alex Morgan left a comment" / "Alex Morgan left 3 comments" / "2 users
    left comments"; null with none. Counted by person; the count of comments
    shows only when one person left them all. Takes `writtenComments`. */
export function commentsPillLabel(comments: readonly WrittenComment[]): string | null {
  if (comments.length === 0) return null
  const people = distinctPeople(comments)
  if (people.length > 1) return `${people.length} users left comments`
  return comments.length === 1 ? `${people[0]} left a comment` : `${people[0]} left ${comments.length} comments`
}

/** "3 unresolved" beside the Flags heading: yellow above zero, green at zero. */
export function unresolvedPill(count: number): { label: string; pill: PillColors } {
  return { label: `${count} unresolved`, pill: count > 0 ? TINT_PILLS.yellow : TINT_PILLS.green }
}
